/**
 * Arabic strings for the whole app.
 *
 * Everything the operator reads lives here rather than inline in the pages, so
 * the wording can be reviewed and corrected in one file by someone who reads
 * Arabic without touching any component.
 *
 * Numbers and dates are deliberately NOT localised: figures stay in Latin
 * digits with thousands separators, and dates stay dd/mm/yyyy. See format.js -
 * every figure is wrapped in a bidirectional isolate so it keeps its order
 * inside right-to-left text.
 */

export const dict = {
  app: {
    name: 'معمل قلعة كركوك',
    tagline: 'لتصفية وتنقية الزيوت المستعملة',
    signInPrompt: 'سجّل الدخول لتسجيل المشتريات والمبيعات والتسديدات.',
    loading: 'جارٍ التحميل',
    opening: 'جارٍ الفتح',
  },

  nav: {
    home: 'الرئيسية',
    buy: 'شراء',
    sell: 'بيع',
    customers: 'الزبائن',
    suppliers: 'الموردون',
    tanks: 'الخزانات',
    expenses: 'المصاريف',
    exchange: 'الصرافة',
    shareholders: 'الشركاء',
    payroll: 'الرواتب',
    reports: 'التقارير',
    settings: 'الإعدادات',
    backup: 'النسخ الاحتياطي',
    more: 'المزيد',
    records: 'السجلات',
    signOut: 'تسجيل الخروج',
    main: 'القائمة الرئيسية',
  },

  common: {
    save: 'حفظ',
    saveChanges: 'حفظ التعديلات',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    add: 'إضافة',
    close: 'إغلاق',
    back: 'رجوع',
    search: 'بحث',
    refresh: 'تحديث',
    retry: 'إعادة المحاولة',
    from: 'من',
    to: 'إلى',
    date: 'التاريخ',
    notes: 'ملاحظات',
    phone: 'الهاتف',
    address: 'العنوان',
    name: 'الاسم',
    status: 'الحالة',
    active: 'فعّال',
    inactive: 'غير فعّال',
    currency: 'العملة',
    amount: 'المبلغ',
    total: 'المجموع',
    quantity: 'الكمية',
    reference: 'الإشارة',
    method: 'طريقة الدفع',
    all: 'الكل',
    allTime: 'كل الفترات',
    none: 'لا يوجد',
    never: 'لم يحدث',
    yes: 'نعم',
    no: 'كلا',
    settled: 'مسدّد',
    unknown: 'غير معروف',
    entries: 'حركة',
    system: 'النظام',
  },

  auth: {
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    signIn: 'تسجيل الدخول',
    signedInAs: 'المستخدم الحالي',
    changePassword: 'تغيير كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور الجديدة',
    passwordsDoNotMatch: 'كلمتا المرور غير متطابقتين',
    passwordMinimum: '8 أحرف على الأقل.',
    passwordChanged: 'تم تغيير كلمة المرور',
  },

  connection: {
    offline: 'غير متصل — تُعرض البيانات المحفوظة فقط.',
    waiting: (n) => ` بانتظار الإرسال: ${n}.`,
    sendNow: 'إرسال الآن',
    view: 'عرض',
    sent: (n) => `تم إرسال الحركات المؤجلة: ${n}`,
    stillWaiting: 'ما زال الاتصال منقطعاً',
    nothingWaiting: 'لا توجد حركات بانتظار الإرسال',
    online: 'متصل',
    savedOffline: 'لا يوجد اتصال — تم حفظ الحركة وستُرسل تلقائياً',
    noConnection: 'لا يوجد اتصال بالخادم',
    cachedNotice:
      'لا يوجد اتصال، ولم تُحفظ بيانات لهذه الشاشة بعد. ستظهر عند عودة الاتصال.',
  },

  dashboard: {
    title: 'الرئيسية',
    soldToday: 'المبيعات اليوم',
    boughtToday: 'المشتريات اليوم',
    customersOweUs: 'ديون الزبائن لنا',
    weOweSuppliers: 'ديوننا للموردين',
    exchangeBalance: 'رصيدنا لدى الصرافات',
    exchangeHeld: 'محتفظ به لدينا',
    exchangeOwed: 'مستحق علينا',
    monthSold: 'مبيعات هذا الشهر',
    monthBought: 'مشتريات هذا الشهر',
    monthExpenses: 'مصاريف هذا الشهر',
    tanks: 'الخزانات',
    manage: 'إدارة',
    latestActivity: 'آخر الحركات',
    allSales: 'كل المبيعات',
    noTanks: 'لا توجد خزانات',
    noTanksHint: 'أضف خزاناً قبل تسجيل أي عملية شراء.',
    nothingYet: 'لا توجد حركات بعد',
    nothingYetHint: 'ستظهر هنا عمليات الشراء والبيع.',
    sale: 'بيع',
    purchase: 'شراء',
    // Arabic agreement changes with 1, 2, 3-10 and 11+, which no simple
    // singular/plural switch gets right. Putting the noun first and the count
    // after the colon sidesteps agreement entirely and stays readable.
    saleCount: (n) => `عمليات البيع: ${n}`,
    loadCount: (n) => `الحمولات: ${n}`,
    accountCount: (n) => `الحسابات: ${n}`,
  },

  purchase: {
    title: 'شراء الديزل',
    subtitle: 'الحمولات الواردة من الموردين',
    newTitle: 'عملية شراء جديدة',
    editTitle: 'تعديل عملية الشراء',
    formSubtitle: 'ديزل مستلم من مورد',
    batchNo: 'رقم الدفعة',
    supplier: 'المورد',
    addSupplier: 'إضافة مورد',
    supplierHint: 'اكتب اسماً جديداً لإضافة مورد دون مغادرة هذه الشاشة.',
    tons: 'الكمية (طن)',
    liters: 'الكمية (لتر)',
    litersHint: 'تُدخل بشكل منفصل — الكثافة تختلف من دفعة لأخرى',
    densityHint: (n) => `${n} لتر لكل طن`,
    destinationTank: 'خزان التفريغ',
    chooseTank: 'اختر الخزاناً',
    roomFor: (v) => `تتسع لـ ${v} إضافية`,
    overflow: (tank) => `هذه الحمولة أكبر من المساحة المتبقية في ${tank}.`,
    load: 'الحمولة',
    price: 'السعر',
    pricePerTon: 'سعر الطن',
    pricePerTonHint: 'يُضرب بعدد الأطنان لحساب المبلغ المقيّد على حساب المورد.',
    computedAmount: (v) => `المبلغ المحتسب: ${v}`,
    save: 'حفظ عملية الشراء',
    saved: (no) => `تم حفظ عملية الشراء ${no}`,
    updated: 'تم تعديل عملية الشراء',
    queued: 'لا يوجد اتصال — تم حفظ عملية الشراء وستُرسل تلقائياً',
    loads: 'الحمولات',
    tonnes: 'الأطنان',
    value: 'القيمة',
    none: 'لا توجد مشتريات في هذه الفترة',
    noneHint: 'غيّر التواريخ، أو سجّل أول حمولة.',
    record: 'تسجيل عملية شراء',
    searchHint: 'رقم الدفعة أو اسم المورد',
    receipt: 'إيصال شراء',
    another: 'عملية شراء أخرى',
    supplierStatement: 'كشف حساب المورد',
    rate: 'السعر للطن',
    deliveredBy: 'سلّمها',
    receivedBy: 'استلمها',
    deleted: 'تم حذف عملية الشراء',
    deleteTitle: 'حذف عملية الشراء',
    deleteMessage: (batchNo) => `هل تريد حذف الدفعة ${batchNo}؟ لا يمكن التراجع عن هذا.`,
  },

  sale: {
    title: 'بيع الديزل',
    subtitle: 'التسليمات إلى الزبائن',
    newTitle: 'عملية بيع جديدة',
    editTitle: 'تعديل عملية البيع',
    formSubtitle: 'ديزل مسلّم إلى زبون',
    receiptNo: 'رقم الإيصال',
    customer: 'الزبون',
    addCustomer: 'إضافة زبون',
    customerHint: 'مطلوب حتى في البيع النقدي، ليبقى كل لتر قابلاً للتتبع.',
    customerOwes: (name, iqd) => `${name} مدين أصلاً بمبلغ ${iqd}`,
    soldBy: 'وحدة البيع',
    barrel: 'برميل',
    ton: 'طن',
    barrels: 'عدد البراميل',
    tonnes: 'عدد الأطنان',
    barrelHint: 'البرميل = 220 لتر',
    barrelsCalculated: 'محسوبة من عدد اللترات',
    sourceTank: 'الخزان المصدر',
    available: (v) => `المتوفر ${v}`,
    shortfall: (tank, v) => `لا يحتوي ${tank} إلا على ${v}. قلّل الكمية أو اختر خزاناً آخر.`,
    pricePerLiter: 'سعر اللتر',
    pricePerLiterHint: 'يُضرب بعدد اللترات لحساب المبلغ المقيّد على حساب الزبون.',
    save: 'حفظ عملية البيع',
    saved: (no) => `تم حفظ عملية البيع ${no}`,
    updated: 'تم تعديل عملية البيع',
    queued: 'لا يوجد اتصال — تم حفظ عملية البيع وستُرسل تلقائياً',
    sales: 'عدد المبيعات',
    volume: 'الكمية',
    revenue: 'الإيرادات',
    none: 'لا توجد مبيعات في هذه الفترة',
    noneHint: 'غيّر التواريخ، أو سجّل أول عملية تسليم.',
    record: 'تسجيل عملية بيع',
    searchHint: 'رقم الإيصال أو اسم الزبون',
    receipt: 'إيصال بيع',
    batch: 'الدفعة',
    allBatches: 'كل الدفعات',
    noBatch: 'غير مرتبط بدفعة',
    batchOptional: 'اختياري — اربط هذا البيع بدفعة شراء معينة لتظهر في تقرير ربحية الدفعات.',
    batchTankMismatch: (batchNo) => `هذه الدفعة (${batchNo}) وردت في خزان مختلف عن خزان هذا البيع — تحقق أنه الاختيار الصحيح.`,
    another: 'عملية بيع أخرى',
    customerStatement: 'كشف حساب الزبون',
    receivedBy: 'استلمها',
    authorisedSignature: 'التوقيع المعتمد',
    deleted: 'تم حذف عملية البيع',
    deleteTitle: 'حذف عملية البيع',
    deleteMessage: (receiptNo) => `هل تريد حذف عملية البيع ${receiptNo}؟ لا يمكن التراجع عن هذا.`,
  },

  shared: {
    ref: 'الرقم',
    party: 'الجهة',
    tank: 'الخزان',
    pricedIn: 'العملة المعتمدة',
    currencyHint: 'هذه العملة وحدها تؤثر على الرصيد. لا يجري أي تحويل بين العملات.',
    amountIqd: 'المبلغ (دينار)',
    amountUsd: 'المبلغ (دولار)',
    chargedToSupplier: 'يُقيَّد على حساب المورد',
    chargedToCustomer: 'يُقيَّد على حساب الزبون',
    referenceOnly: 'للاستدلال فقط',
    payment: 'طريقة التسديد',
    searchOrType: 'ابحث أو اكتب اسماً',
    required: 'مطلوب',
    cash: 'نقداً',
    debt: 'آجل',
    issued: (date) => `صدر في ${date}`,
    issuedBy: (name) => `بواسطة ${name}`,
    reference: 'المبلغ للاستدلال',
  },

  party: {
    customers: {
      title: 'الزبائن',
      subtitle: 'من يشتري منّا، وما بقي عليه',
      one: 'زبون',
      balanceLabel: 'مدين لنا',
      totalLabel: 'مجموع ديون الزبائن',
      paymentTitle: 'تسجيل مبلغ مستلم',
      paymentHint: 'مبلغ مستلم من هذا الزبون على حساب رصيده.',
      newTitle: 'زبون جديد',
      editTitle: 'تعديل بيانات الزبون',
      emptyTitle: 'لا يوجد زبائن بعد',
      emptyHint: 'أضف زبوناً هنا، أو اكتب اسماً جديداً مباشرة في شاشة البيع.',
      addOne: 'إضافة زبون',
      statementOwed: 'مدين لنا',
    },
    suppliers: {
      title: 'الموردون',
      subtitle: 'ممن نشتري، وما بقي لهم علينا',
      one: 'مورد',
      balanceLabel: 'مدينون له',
      totalLabel: 'مجموع ديوننا',
      paymentTitle: 'تسجيل مبلغ مدفوع',
      paymentHint: 'مبلغ مدفوع لهذا المورد على حساب رصيده.',
      newTitle: 'مورد جديد',
      editTitle: 'تعديل بيانات المورد',
      emptyTitle: 'لا يوجد موردون بعد',
      emptyHint: 'أضف مورداً هنا، أو اكتب اسماً جديداً مباشرة في شاشة الشراء.',
      addOne: 'إضافة مورد',
      statementOwed: 'مدينون له',
    },
    accounts: 'عدد الحسابات',
    allCustomers: 'كل الزبائن',
    allSuppliers: 'كل الموردين',
    searchHint: 'الاسم أو رقم الهاتف',
    statement: 'كشف الحساب',
    payment: 'تسديد',
    saved: (name) => `تم حفظ بيانات ${name}`,
    paymentSaved: 'تم تسجيل التسديد',
    invoiceCurrency: 'عملة الفوترة المعتادة',
    invoiceCurrencyHint: 'مجرد قيمة افتراضية للحركات الجديدة.',
    either: 'أي منهما',
    outstandingIn: (cur) => `الرصيد المتبقي بـ${cur}`,
    deleted: (name) => `تم حذف ${name}`,
    deleteTitle: 'حذف الحساب',
    deleteMessage: (name) => `هل تريد حذف "${name}"؟ لا يمكن التراجع عن هذا.`,
  },

  statement: {
    title: 'كشف حساب',
    detail: 'البيان',
    chargeIqd: 'مدين دينار',
    paidIqd: 'دائن دينار',
    balanceIqd: 'الرصيد دينار',
    chargeUsd: 'مدين دولار',
    paidUsd: 'دائن دولار',
    balanceUsd: 'الرصيد دولار',
    opening: 'الرصيد الافتتاحي',
    noTransactions: 'لا توجد حركات في هذه الفترة',
    note: 'الدينار والدولار يُحسبان بشكل منفصل ولا يجري أي تحويل بينهما. الحركة النقدية تظهر في الكشف لكنها لا تغيّر الرصيد.',
    entryCount: (n) => `عدد الحركات: ${n}`,
    start: 'البداية',
    today: 'اليوم',
  },


  tank: {
    title: 'الخزانات',
    subtitle: 'المستويات محسوبة من المشتريات والمبيعات',
    newTitle: 'خزان جديد',
    editTitle: 'تعديل الخزان',
    tankName: 'اسم الخزان',
    capacity: 'السعة (لتر)',
    openingVolume: 'الكمية الافتتاحية (لتر)',
    openingHint:
      'ما كان موجوداً في الخزان قبل بدء التسجيل في هذا النظام. كل تغيير بعدها يأتي من المشتريات والمبيعات.',
    inStorage: 'المخزون الحالي',
    ofCapacity: (v) => `من أصل ${v} سعة`,
    spaceFree: 'المساحة المتاحة',
    tankCount: (n) => `عدد الخزانات: ${n}`,
    movements: 'الحركات',
    movementsOf: (name) => `حركات ${name}`,
    noMovements: 'لا توجد حركات بعد',
    direction: 'الاتجاه',
    in: 'وارد',
    out: 'صادر',
    emptyTitle: 'لا توجد خزانات بعد',
    emptyHint: 'لا يمكن استلام الديزل قبل إضافة خزان.',
    addOne: 'إضافة خزان',
    saved: (name) => `تم حفظ ${name}`,
    of: (v) => `من أصل ${v}`,
    deleted: 'تم حذف الخزان',
    deleteTitle: 'حذف الخزان',
    deleteMessage: (name) => `هل تريد حذف "${name}"؟ لا يمكن التراجع عن هذا.`,
  },

  expense: {
    title: 'المصاريف',
    subtitle: 'المصاريف التشغيلية حسب النوع',
    newTitle: 'مصروف جديد',
    editTitle: 'تعديل المصروف',
    category: 'النوع',
    categoryHint: 'اختر نوعاً من القائمة أو اكتب نوعاً جديداً.',
    allCategories: 'كل الأنواع',
    description: 'البيان',
    paidBy: 'دفعها',
    paidByShort: (name) => `دفعها ${name}`,
    entryCount: 'عدد الحركات',
    totalIqd: 'المجموع بالدينار',
    totalUsd: 'المجموع بالدولار',
    byCategory: 'حسب النوع',
    none: 'لا توجد مصاريف في هذه الفترة',
    addOne: 'إضافة مصروف',
    saved: 'تم حفظ المصروف',
    deleted: 'تم حذف المصروف',
    deleteTitle: 'حذف المصروف',
    deleteMessage: (amount, date) => `هل تريد حذف ${amount} بتاريخ ${date}؟ لا يمكن التراجع عن هذا.`,
    paidVia: 'طريقة الدفع',
    viaExchangeCompany: 'عبر شركة صرافة',
    paidViaCompany: (name) => `دُفع عبر ${name}`,
    exchangeKind: 'نوع العملية',
    exchangeKindTransfer: 'الشركة دفعت مباشرة',
    exchangeKindDeposit: 'حُصِّل نقداً من الشركة',
    exchangeKindWithdrawal: 'سُحب نقداً من رصيدنا لدى الشركة',
    exchangeKindTransferHint: 'الشركة تدفع للجهة المستفيدة نيابة عنا؛ يُقيَّد المبلغ ديناً علينا لدى الشركة.',
    exchangeKindDepositHint: 'حصّلنا نقداً من رصيدنا لدى الشركة لتغطية هذا المصروف.',
    exchangeKindWithdrawalHint: 'سُحب المبلغ من رصيدنا لدى الشركة وصُرف مباشرة على هذا المصروف.',
  },

  payroll: {
    title: 'الرواتب',
    subtitle: 'الرواتب الشهرية الثابتة وما تم دفعه منها',
    month: 'شهر الرواتب',
    staffCount: 'عدد الموظفين',
    unpaidThisMonth: 'لم تُدفع رواتبهم هذا الشهر',
    monthStatus: (month) => `حالة شهر ${month}`,
    paid: 'مدفوع',
    unpaid: 'غير مدفوع',
    recordPayment: 'تسجيل دفعة',
    payStaff: (name) => `دفع راتب ${name}`,
    staff: 'الموظفون',
    role: 'الوظيفة',
    salary: 'الراتب',
    monthlySalary: 'الراتب الشهري',
    salaryCurrency: 'عملة الراتب',
    hired: 'تاريخ التعيين',
    hireDate: 'تاريخ التعيين',
    lastPaid: 'آخر دفعة',
    recentPayments: 'آخر دفعات الرواتب',
    paidOn: 'تاريخ الدفع',
    covers: 'عن شهر',
    monthCovered: 'الشهر المشمول',
    monthCoveredHint: 'يمكن تسجيل دفعة جزئية أو سلفة مع تحديد الشهر الذي تخصّه.',
    noStaff: 'لا يوجد موظفون فعّالون',
    noStaffHint: 'أضف موظفين لبدء متابعة الرواتب.',
    noStaffOnFile: 'لا يوجد موظفون مسجلون',
    noPayments: 'لا توجد دفعات رواتب مسجلة',
    newStaff: 'موظف جديد',
    editStaff: 'تعديل بيانات الموظف',
    staffSaved: 'تم حفظ بيانات الموظف',
    staffDeleted: 'تم حذف الموظف',
    deleteStaff: 'حذف الموظف',
    deleteStaffMessage: (name) =>
      `هل تريد حذف "${name}"؟ سيُحذف سجل رواتبه بالكامل معه، ولا يمكن التراجع عن هذا.`,
    paymentSaved: 'تم تسجيل دفعة الراتب',
    rolePlaceholder: 'مشغّل مضخة',
  },

  report: {
    title: 'التقارير',
    subtitle: 'ملخص الفترة والمنحنيات وأعمار الديون',
    quickRange: 'فترات سريعة',
    days7: '7 أيام',
    days30: '30 يوماً',
    year: 'السنة',
    periodReport: 'تقرير الفترة',
    line: 'البند',
    iqd: 'دينار',
    usd: 'دولار',
    volumeCol: 'الكمية',
    dieselSold: (n) => `الديزل المباع (${n})`,
    dieselBought: (n) => `الديزل المشترى (${n})`,
    expenses: 'المصاريف',
    salariesPaid: 'الرواتب المدفوعة',
    netForPeriod: 'صافي الفترة',
    netNote:
      'الصافي هو الوارد ناقص الصادر خلال هذه الفترة، وليس كلفة البضاعة المباعة. الديزل المشترى في شهر والمباع في الشهر التالي ينقل الرقم بين الفترتين — لذلك تظهر اللترات الواردة والصادرة إلى جانبه ليتضح الفرق.',
    settlements: 'التسديدات',
    receivedFromCustomers: 'المستلم من الزبائن',
    paidToSuppliers: 'المدفوع للموردين',
    soldOnCredit: 'المباع بالآجل',
    boughtOnCredit: 'المشترى بالآجل',
    sold: 'المبيعات',
    bought: 'المشتريات',
    netIqd: 'الصافي بالدينار',
    netUsd: 'الصافي بالدولار',
    dailyVolume: 'الكميات اليومية',
    boughtLegend: 'مشتريات',
    soldLegend: 'مبيعات',
    peakDay: (v) => `أعلى يوم: ${v}`,
    dailyAria: 'اللترات المشتراة والمباعة يومياً',
    topCustomers: 'أكثر الزبائن',
    topSuppliers: 'أكثر الموردين',
    count: 'العدد',
    litersCol: 'اللترات',
    valueCol: 'القيمة',
    nothingInPeriod: 'لا توجد بيانات في هذه الفترة',
    aging: 'أعمار الديون',
    agingNote: (date) =>
      `تُطبَّق التسديدات على أقدم مبلغ غير مسدّد أولاً، لأن التسديد غير مرتبط بفاتورة بعينها. تُحسب الأعمار بتاريخ ${date}.`,
    account: 'الحساب',
    days: 'يوم',
    oldest: 'الأقدم',
    nothingOutstanding: 'لا توجد ديون قائمة',
  },

  settings: {
    title: 'الإعدادات',
    facilityDetails: 'بيانات المعمل',
    facilityName: 'اسم المعمل',
    facilityFooterNote: 'تظهر هذه البيانات في كل إيصال وكشف حساب.',
    receiptFooter: 'عبارة أسفل الإيصال',
    whatsappCode: 'رمز الدولة في واتساب',
    whatsappCodeHint: 'يُستخدم عندما يبدأ الرقم المحفوظ بصفر، مثل 964 للعراق.',
    saveDetails: 'حفظ البيانات',
    detailsSaved: 'تم حفظ بيانات المعمل',
    users: 'المستخدمون',
    noUsers: 'لا يوجد مستخدمون',
    fullName: 'الاسم الكامل',
    role: 'الصلاحية',
    admin: 'مدير',
    staffRole: 'موظف',
    roleStaff: 'موظف — يسجّل كل الحركات',
    roleAdmin: 'مدير — يسجّل الحركات ويحذف ويدير المستخدمين',
    disabled: 'موقوف',
    newUser: 'مستخدم جديد',
    editUser: (name) => `تعديل ${name}`,
    userSaved: 'تم حفظ المستخدم',
    userDeleted: 'تم حذف المستخدم',
    deleteUser: 'حذف المستخدم',
    deleteUserMessage: (username) => `هل تريد حذف "${username}"؟ لا يمكن التراجع عن هذا.`,
    newPasswordOptional: 'كلمة مرور جديدة (اتركها فارغة للإبقاء عليها)',
    install: 'التثبيت على هذا الجهاز',
    installed: 'التطبيق مثبّت ويعمل كتطبيق مستقل.',
    installPrompt: 'ثبّت التطبيق لفتحه من الشاشة الرئيسية ومتابعة العمل عند انقطاع الاتصال.',
    installButton: 'تثبيت',
    installManual:
      'على أندرويد افتح قائمة المتصفح واختر «إضافة إلى الشاشة الرئيسية». على الآيفون استخدم زر المشاركة في سفاري ثم «إضافة إلى الشاشة الرئيسية».',
  },

  backup: {
    title: 'النسخ الاحتياطي والاستعادة',
    subtitle: 'خذ نسخة من كل شيء، أو أعد نسخة سابقة',
    adminOnly:
      'النسخ الاحتياطي والاستعادة غير متاحين من هذا الحساب.',
    recordsInDb: 'عدد السجلات في قاعدة البيانات',
    snapshotsOnServer: 'النسخ المحفوظة على الخادم',
    newestKept: (kept, keep) => `يُحتفظ بأحدث ${kept} من أصل ${keep}`,
    lastBackup: 'آخر نسخة',
    takeBackup: 'أخذ نسخة احتياطية',
    contentsNote:
      'تحتوي النسخة على كل عمليات الشراء والبيع والتسديدات والمصاريف والخزانات والسائقين والموظفين، إضافة إلى إعدادات المعمل وحسابات المستخدمين. استعادة أي نسخة تستبدل كل ما هو موجود حالياً.',
    passwordWarning:
      'يحتوي الملف على حسابات المستخدمين وكلمات مرورهم المشفّرة. تعامل معه كمفتاح القاصة: احفظه في مكان خاص ولا ترسله لمن لا يحق له الاطلاع الكامل.',
    downloadToDevice: 'تنزيل نسخة على هذا الجهاز',
    saveOnServer: 'حفظ نسخة على الخادم',
    serverNote:
      'النسخة المحفوظة على الخادم مريحة لكنها على نفس جهاز قاعدة البيانات. نزّل نسخة أيضاً، وأبقِ نسخة pg_dump الليلية تعمل للنسخ خارج الجهاز.',
    whatItContains: 'محتويات النسخة الاحتياطية',
    recordType: 'نوع السجل',
    rows: 'عدد السجلات',
    noSnapshots: 'لا توجد نسخ محفوظة بعد',
    noSnapshotsHint: 'احفظ نسخة من الأعلى، أو جدول أمر النسخ على الخادم.',
    safetyCopy: 'أُخذت قبل استعادة',
    download: 'تنزيل',
    restoreThis: 'استعادة هذه النسخة',
    deleteSnapshot: 'حذف النسخة',
    deleteMessage: (name) => `سيُحذف ${name} من الخادم. إن كانت النسخة الوحيدة فنزّلها أولاً.`,
    restoreFromFile: 'الاستعادة من ملف',
    restoreFromFileHint: 'اختر نسخة نزّلتها سابقاً. لن يتغير شيء قبل التأكيد في الشاشة التالية.',
    chooseFile: 'اختر ملف النسخة الاحتياطية',
    history: 'السجل',
    nothingRecorded: 'لا توجد عمليات مسجلة بعد',
    when: 'الوقت',
    action: 'العملية',
    by: 'بواسطة',
    file: 'الملف',
    note: 'ملاحظة',
    actions: { export: 'تنزيل', snapshot: 'حفظ', restore: 'استعادة', prune: 'تنظيف' },
    restoreTitle: 'استعادة هذه النسخة',
    restoreWarning:
      'سيُحذف كل ما هو مسجّل حالياً من مشتريات ومبيعات وتسديدات ومصاريف وحسابات مستخدمين، ويُستبدل بمحتوى هذا الملف. كل ما سُجّل بعد تاريخ هذه النسخة سيضيع.',
    restoreSafety: 'تُحفظ نسخة من السجلات الحالية على الخادم أولاً، ليمكن التراجع عن أي خطأ باستعادتها.',
    reading: 'جارٍ قراءة النسخة',
    takenAt: 'تاريخ النسخة',
    takenBy: 'أخذها',
    facility: 'المعمل',
    records: 'عدد السجلات',
    contents: 'المحتويات',
    typeToConfirm: 'اكتب RESTORE للتأكيد',
    replaceAll: 'استبدال كل السجلات',
    restored: (n) => `تمت استعادة السجلات: ${n}`,
    snapshotSaved: (name) => `تم حفظ النسخة على الخادم باسم ${name}`,
    downloaded: (name) => `تم تنزيل ${name}`,
    snapshotDeleted: 'تم حذف النسخة',
    notReadable: 'تعذّرت قراءة هذا الملف كنسخة احتياطية',
    empty: 'فارغة',
    openBackup: 'فتح النسخ الاحتياطي والاستعادة',
    lastBackupLine: (when, who) => `آخر نسخة ${when} بواسطة ${who}.`,
    noBackupYet: 'لم تؤخذ أي نسخة احتياطية بعد.',
    noBackupWarning: 'خذ نسخة احتياطية الآن — لا توجد نسخة من هذه السجلات في أي مكان آخر.',
    tables: {
      users: 'حسابات المستخدمين',
      app_settings: 'إعدادات المعمل',
      suppliers: 'الموردون',
      customers: 'الزبائن',
      staff: 'الموظفون',
      tanks: 'الخزانات',
      purchases: 'المشتريات',
      sales: 'المبيعات',
      payments: 'التسديدات',
      expenses: 'المصاريف',
      salary_payments: 'دفعات الرواتب',
      backup_log: 'سجل النسخ الاحتياطي',
    },
  },

  queue: {
    title: 'الحركات بانتظار الإرسال',
    subtitle: 'حركات محفوظة على هذا الجهاز',
    waiting: 'بانتظار الإرسال',
    rejected: 'مرفوضة',
    connection: 'الاتصال',
    rejectedNotice: (n) =>
      `رفض الخادم عدداً من الحركات (${n}). عالج السبب (مثل خزان لا يحتوي كمية كافية) ثم أعد الإرسال، أو احذف الحركة وأعد إدخالها.`,
    nothingWaiting: 'لا توجد حركات بانتظار الإرسال',
    nothingWaitingHint: 'كل ما سُجّل على هذا الجهاز وصل إلى الخادم.',
    attempts: (n) => `المحاولات: ${n}`,
    discard: 'حذف',
    discardTitle: 'حذف هذه الحركة',
    discardMessage: (label) =>
      `سيُحذف «${label}» من هذا الجهاز ولن يُرسل أبداً. أعد إدخاله إن كان ما زال مطلوباً.`,
    discarded: 'تم حذف الحركة',
    rejectedToast: 'رُفضت بعض الحركات — راجع الأسباب أدناه',
  },

  exchange: {
    title: 'الصرافة',
    subtitle: 'الإيداع والسحب والحوالات وبيع وشراء الدولار',
    companies: 'شركات الصرافة',
    company: 'شركة الصرافة',
    addCompany: 'إضافة شركة صرافة',
    newCompany: 'شركة صرافة جديدة',
    editCompany: 'تعديل بيانات الشركة',
    searchHint: 'اسم الشركة أو رقم الهاتف',
    emptyTitle: 'لا توجد شركات صرافة بعد',
    emptyHint: 'أضف شركة الصرافة التي تتعامل معها لتبدأ بتسجيل الإيداعات والحوالات.',
    balanceLabel: 'الرصيد لدى الشركة',
    heldByThem: 'محتفظ به لدينا',
    weOwe: 'مستحق عليها',
    totalHeld: 'مجموع المحتفظ به لدى الصرافات',
    totalOwed: 'مجموع مستحق علينا للصرافات',
    accountNote:
      'الرصيد الموجب يعني أن الشركة تحتفظ بهذا المبلغ لحسابنا. الرصيد السالب يعني أننا مدينون لها بهذا المبلغ — وهذا ما تنتجه حوالة تُطلب دون تغطية كافية.',
    statement: 'كشف الحساب',
    newTransaction: 'عملية جديدة',
    editTransaction: 'تعديل العملية',
    transactionSaved: 'تم حفظ العملية',
    transactionDeleted: 'تم حذف العملية',
    deleteTransaction: 'حذف العملية',
    deleteTransactionMessage: 'سيُحذف هذا القيد نهائياً من كشف الحساب. هل تريد المتابعة؟',
    companySaved: (name) => `تم حفظ بيانات ${name}`,
    companyDeleted: 'تم حذف شركة الصرافة',
    deleteCompany: 'حذف شركة الصرافة',
    deleteCompanyMessage: (name) => `هل تريد حذف "${name}"؟ لا يمكن التراجع عن هذا.`,
    companyDeleteBlocked: 'لهذه الشركة حركات مسجلة ولا يمكن حذفها. اجعلها غير فعّالة بدلاً من ذلك.',

    // Kinds
    kind: 'نوع العملية',
    kindLocked: 'لا يمكن تغيير نوع العملية بعد إنشائها — احذفها وأنشئ عملية جديدة بدلاً من ذلك.',
    deposit: 'إيداع',
    withdrawal: 'سحب',
    transfer: 'حوالة',
    buy_usd: 'شراء دولار',
    sell_usd: 'بيع دولار',
    depositHint: 'مبلغ يُسلَّم إلى شركة الصرافة لتحتفظ به لحسابنا.',
    withdrawalHint: 'مبلغ يُسحَب مما تحتفظ به الشركة لحسابنا.',
    transferHint: 'حوالة تُرسلها الشركة نيابة عنا. تُقيَّد على حسابنا لدى الشركة بكامل مبلغها بالإضافة إلى العمولة.',
    buyUsdHint: 'دينار يخرج من الرصيد لدى الشركة، ودولار يدخل بدلاً منه.',
    sellUsdHint: 'دولار يخرج من الرصيد لدى الشركة، ودينار يدخل بدلاً منه.',

    // Fields
    date: 'التاريخ',
    amount: 'المبلغ',
    currency: 'العملة',
    commission: 'عمولة الصرافة',
    commissionHint: 'ما تتقاضاه الشركة مقابل الخدمة. اتركها صفراً إن لم توجد عمولة.',
    commissionCurrency: 'عملة العمولة',
    reference: 'الإشارة',
    referenceHint: 'رقم الوصل أو أي رمز مرجعي، اختياري.',
    beneficiary: 'اسم المستفيد',
    beneficiaryHint: 'إن لم يكن المستفيد مورداً مسجلاً لدينا، اكتب اسمه هنا.',
    orChooseSupplier: 'أو اختر المورد الذي تُسدَّد له الحوالة',
    supplierPaidNote: (name) => `ستُسدَّد هذه الحوالة إلى حساب المورد ${name} وتُخفِّض ديننا له.`,
    withdrawalSupplierPaidNote: (name) => `سيُسدَّد هذا السحب إلى حساب المورد ${name} وتُخفِّض ديننا له.`,
    collectedFromCustomer: 'مبلغ مستلم من زبون؟',
    collectedFromCustomerHint: 'إن كان هذا المبلغ نقداً استُلم من زبون وسُلِّم للصرافة مباشرة، اربطه بحسابه ليُسدَّد دينه.',
    customerPaidNote: (name) => `سيُسدَّد هذا الإيداع من دين الزبون ${name}.`,
    transferDestination: 'وجهة الحوالة',
    shareholderPaidNote: (name) => `ستُسدَّد هذه الحوالة إلى حساب الشريك ${name} وتُخفِّض رصيده المستحق.`,
    payrollDestination: 'راتب موظف',
    payrollMonth: 'شهر الراتب',
    payrollPaidNote: (name) => `ستُسجَّل هذه الحوالة كراتب مدفوع للموظف ${name}.`,
    depositLink: 'ربط الإيداع بحساب؟',
    depositLinkNone: 'بدون ربط',
    shareholderDepositNote: (name) => `سيُسجَّل هذا الإيداع كمساهمة رأس مال من الشريك ${name} ويزيد رصيده المستحق.`,
    withdrawalLink: 'ربط السحب بمورد؟',
    withdrawalLinkHint: 'إن كان هذا المبلغ المسحوب قد سُلِّم نقداً لمورد، اربطه بحسابه ليُسدَّد دينه.',
    rate: 'سعر الصرف',
    rateHint: 'دينار لكل دولار واحد.',
    usdAmount: 'مبلغ الدولار',
    iqdAmount: 'المبلغ بالدينار',
    iqdAmountComputed: 'محسوب تلقائياً من السعر × مبلغ الدولار',
    noDestination: 'أكمل بيانات وجهة الحوالة أعلاه قبل الحفظ',

    // List / filters
    allCompanies: 'كل الشركات',
    allKinds: 'كل الأنواع',
    recentTransactions: 'الحركات',
    noTransactions: 'لا توجد حركات في هذه الفترة',
    entryCount: 'عدد الحركات',
    totalCommission: 'مجموع العمولات',
    totalDeposits: 'مجموع الإيداعات',
    totalWithdrawals: 'مجموع السحوبات',
    totalTransfers: 'مجموع الحوالات',
    totalBought: 'مجموع الدولار المشترى',
    totalSold: 'مجموع الدولار المباع',

    // Statement description helpers (client-side, for the WhatsApp text)
    statementOwed: 'الرصيد لدينا',
  },

  openingBalance: {
    title: 'رصيد افتتاحي',
    action: 'رصيد افتتاحي',
    save: 'حفظ الرصيد الافتتاحي',
    clear: 'إزالة الرصيد الافتتاحي',
    clearConfirm: 'سيُحذف هذا الرصيد الافتتاحي نهائياً. هل تريد المتابعة؟',
    date: 'اعتباراً من تاريخ',
    dateHint: 'تاريخ آخر تحديث للسجلات الورقية القديمة.',
    iqd: 'الرصيد بالدينار',
    usd: 'الرصيد بالدولار',
    note: 'ملاحظة',
    saved: 'تم حفظ الرصيد الافتتاحي',
    cleared: 'تم حذف الرصيد الافتتاحي',
    hintCustomer: 'أدخل مبلغاً موجباً إن كان الزبون مديناً لنا، أو سالباً إن كنا مدينين له.',
    hintSupplier: 'أدخل مبلغاً موجباً إن كنا مدينين لهذا المورد، أو سالباً إن كان هو المدين لنا.',
    hintExchange: 'أدخل مبلغاً موجباً إن كانت الشركة تحتفظ بهذا المبلغ لحسابنا، أو سالباً إن كنا مدينين لها.',
  },

  batchProfit: {
    title: 'ربحية الدفعات',
    note: 'التكلفة من سعر الشراء، والإيراد من المبيعات المرتبطة بهذه الدفعة فقط — الفرق هو الربح. التغطية الجزئية طبيعية إن لم تُباع الدفعة بالكامل بعد.',
    allSuppliers: 'كل الموردين',
    searchHint: 'رقم الدفعة',
    none: 'لا توجد دفعات في هذه الفترة',
    coverage: 'نسبة البيع',
    cost: 'التكلفة',
    revenue: 'الإيراد',
    profit: 'الربح',
    flags: 'ملاحظات',
    mixedCurrency: 'عملتان مختلفتان',
    overAllocated: 'مبيع أكثر من المشترى',
    tankMismatch: 'خزان مختلف',
    undistributed: 'ربح غير موزّع',
    stale: 'تغيّر منذ آخر توزيع',
    distribute: 'توزيع الأرباح',
    distributeTitle: (batchNo) => `توزيع ربح الدفعة ${batchNo}`,
    distributePreviewNote: (amount) => `المبلغ غير الموزّع حالياً: ${amount}. هذا هو التقسيم بحسب حصص الشركاء الفعّالين.`,
    confirmDistribute: 'تأكيد التوزيع',
    nothingToDistribute: 'لا يوجد شركاء فعّالون لهم حصة من الأرباح حالياً',
    unallocated: 'غير مخصّص (يبقى محتفظاً به)',
    distributed: (batchNo) => `تم توزيع ربح الدفعة ${batchNo}`,
  },

  shareholder: {
    title: 'الشركاء',
    subtitle: 'حصص الشركاء في العمل، ورأس المال والأرباح المستحقة لكل منهم',
    accounts: 'عدد الشركاء',
    addOne: 'إضافة شريك',
    newTitle: 'شريك جديد',
    editTitle: 'تعديل بيانات الشريك',
    searchHint: 'اسم الشريك أو رقم الهاتف',
    emptyTitle: 'لا يوجد شركاء بعد',
    emptyHint: 'أضف الشركاء وحصة كل منهم لتبدأ بتوزيع الأرباح وتسجيل حركاتهم.',
    cutPercent: 'حصة الشريك',
    cutPercentHint: 'النسبة المئوية من الأرباح المستحقة لهذا الشريك عند كل توزيع.',
    balanceLabel: 'رصيد الشريك',
    weOwe: 'مستحق له',
    settled: 'مسدّد',
    totalEquity: 'إجمالي المستحق للشركاء',
    saved: (name) => `تم حفظ بيانات ${name}`,
    deleted: 'تم حذف الشريك',
    deleteTitle: 'حذف الشريك',
    deleteMessage: (name) => `هل تريد حذف "${name}"؟ لا يمكن التراجع عن هذا.`,
    deleteBlocked: 'لهذا الشريك حركات مسجلة ولا يمكن حذفه. اجعله غير فعّال بدلاً من ذلك.',
    statement: 'كشف الحساب',
    accountNote: 'الرصيد الموجب يعني أن العمل مدين لهذا الشريك (رأس ماله وأرباحه المستحقة). الرصيد السالب نادر ويعني أنه سحب أكثر مما له.',

    newTransaction: 'حركة جديدة',
    editTransaction: 'تعديل الحركة',
    transactionSaved: 'تم حفظ الحركة',
    transactionDeleted: 'تم حذف الحركة',
    deleteTransaction: 'حذف الحركة',
    deleteTransactionMessage: 'سيُحذف هذا القيد نهائياً من كشف الحساب. هل تريد المتابعة؟',
    kind: 'نوع الحركة',
    contribution: 'مساهمة رأس مال',
    payout: 'دفعة للشريك',
    adjustment: 'تسوية',
    contributionHint: 'مبلغ يقدّمه الشريك للعمل — يزيد ما هو مستحق له.',
    payoutHint: 'مبلغ يُدفع للشريك من رصيده المستحق.',
    adjustmentHint: 'تصحيح يدوي: أدخل رقماً موجباً لزيادة رصيده، أو سالباً لتخفيضه.',
    paymentType: 'طريقة الدفع',
    paymentCash: 'نقداً',
    paymentCompany: 'عبر شركة صرافة',
    company: 'شركة الصرافة',
    allShareholders: 'كل الشركاء',
    allKinds: 'كل الأنواع',
    recentTransactions: 'الحركات',
    noTransactions: 'لا توجد حركات في هذه الفترة',
    totalContributions: 'مجموع المساهمات',
    totalPayouts: 'مجموع الدفعات',
    statementOwed: 'رصيده لدينا',
  },

  doc: {
    saleReceipt: 'إيصال بيع',
    purchaseReceipt: 'إيصال شراء',
    accountStatement: 'كشف حساب',
    periodReport: 'تقرير فترة',
    sendWhatsApp: 'إرسال عبر واتساب',
    downloadPdf: 'تنزيل PDF',
    print: 'طباعة',
    pdfSaved: 'تم حفظ ملف PDF',
    pdfFailed: (msg) => `تعذّر إنشاء ملف PDF: ${msg}`,
    period: 'الفترة',
    thankYou: 'شكراً لتعاملكم معنا.',
  },
};

/** Direction-aware document setup, applied once at start-up. */
export function applyDocumentLanguage() {
  document.documentElement.lang = 'ar';
  document.documentElement.dir = 'rtl';
}

// --- Labels for values stored in the database ------------------------------
// The stored values stay in English so they remain stable identifiers; only the
// display text is Arabic.

export const currencyLabel = { IQD: 'دينار', USD: 'دولار' };

export const paymentTypeLabel = { cash: 'نقداً', debt: 'آجل' };

export const paymentTypeReceipt = { cash: 'نقداً — مسدّد', debt: 'على الحساب (آجل)' };

export const paymentMethodLabel = {
  cash: 'نقداً',
  transfer: 'حوالة مصرفية',
  cheque: 'صك',
  other: 'أخرى',
};

export const unitLabel = { ton: 'طن', barrel: 'برميل' };

export const roleLabel = { admin: 'مدير', staff: 'موظف', superadmin: 'مدير عام' };

export const expenseCategoryLabel = {
  fuel: 'وقود',
  maintenance: 'صيانة',
  utilities: 'خدمات',
  transport: 'نقل',
  rent: 'إيجار',
  salaries: 'رواتب',
  government: 'رسوم حكومية',
  misc: 'متفرقات',
};

/** Falls back to the stored value for categories operators typed themselves. */
export const categoryText = (value) => expenseCategoryLabel[value] || value || '';

export const monthNames = [
  'كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
  'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول',
];

export const t = dict;
